import { loading, rightArrow, search } from "@simplybusiness/icons";
import { Button, Icon, Link, TextField } from "@simplybusiness/mobius";
import {
  type TradeOption,
  type TradeSelectionMeta,
  type TradeSelectorProps,
  useFetchTrades,
} from "@simplybusiness/mobius-trade-selector";
import { useSnowplowContext } from "@simplybusiness/services";
import { useEffect, useId, useRef, useState } from "react";
import { useQuestionnaireActions } from "../../../../../../../../features/questionnaire";
import { useEnvironmentValues } from "../../../../../../../../hooks/environment";
import { useLocale } from "../../../../../../../../hooks/useLocale";
import { QuestionLabel } from "../../../../QuestionLabel";
import type { QuestionComponentProps } from "../../../types";
import {
  AmbiguousTradeModal,
  useAmbiguousTradeInterceptor,
} from "../AmbiguousTradeModal";
import {
  triggerTradeSelectorClicked,
  triggerTradeChanged,
  triggerAiFindMyBusiness,
} from "../events";
import { redirect } from "../redirect";
import {
  convertTradesToOptions,
  EMPTY_TRADE_OPTION,
  getTradeOptionFromAnswer,
} from "../utils";
import { TradesResults } from "./index";
import "./AiTradeQuestion.css";
import { TradeQuestionSelected } from "../TradeQuestionSelected";

const STRINGS = {
  defaultPlaceholder: "Describe your business in a few words",
  emptySearchError: "Please describe your business",
  noMatchError:
    "We are unable to find a match for your search. Try a different description.",
  fetchError: "Failed to fetch trades. Please try again.",
  searching: "Searching...",
  findMyBusiness: "Find My Business",
  occupationsPrefix: "Or choose from the ",
  occupationsLinkGB: "full list of occupations",
  occupationsLinkUS: "full list of professions",
} as const;

export const AiTradeQuestion = ({
  questionFieldProps,
  config,
  preFilledSource,
}: QuestionComponentProps) => {
  const {
    onChange,
    name,
    questionLabelProps,
    errorMessage,
    value,
    placeholder,
  } = questionFieldProps;
  const {
    occupationsPageUrl,
    tradeDefinition,
    allowTradeBasedRedirect,
    ...tradeSelectorConfig
  } = config || {};
  const typedTradeSelectorConfig =
    tradeSelectorConfig as unknown as TradeSelectorProps;
  const answeredTrade = value?.toString() ?? "";

  const { setError, removeError, setVisible, setHidden } =
    useQuestionnaireActions();
  const { site, vertical, affclie, journeyId } = useEnvironmentValues();
  const { snowplow } = useSnowplowContext();
  const { isUK } = useLocale();
  const isUS = site === "simplybusiness_us";
  const labelId = useId();
  const { tradesEndpoint, searchScope, primaryDetail } =
    typedTradeSelectorConfig;

  // State for the search query and selected trade
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTrade, setSelectedTrade] = useState<TradeOption>(
    getTradeOptionFromAnswer(
      answeredTrade,
      tradeDefinition as string | undefined,
    ),
  );
  // Keep selectedTrade in sync with answeredTrade
  useEffect(() => {
    if (answeredTrade && answeredTrade !== selectedTrade.value) {
      setSelectedTrade(
        getTradeOptionFromAnswer(
          answeredTrade,
          tradeDefinition as string | undefined,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answeredTrade]);

  // Fire primaryDetailSelected event once on page load (US only) if trade is pre-populated
  const hasTrackedPageLoad = useRef(false);
  useEffect(() => {
    if (isUS && snowplow && !hasTrackedPageLoad.current && answeredTrade) {
      hasTrackedPageLoad.current = true;
      const params: Record<string, unknown> = {
        answer: answeredTrade,
        selectionMethod: preFilledSource ? "pre-filled" : "genAI",
      };
      if (preFilledSource) {
        params.preFilledSource = preFilledSource;
        params.selectedLocation = null;
      }
      snowplow.trigger("primaryDetailSelected", params);
    }
  }, [isUS, snowplow, answeredTrade, preFilledSource]);

  const tradesMutation = useFetchTrades<TradeOption[]>({
    variant: "ai",
    site,
    vertical: vertical!,
    tradesEndpoint,
    searchScope,
    primaryDetail,
    transformResults: convertTradesToOptions,
  });

  const hasFiredTradeSelectorClicked = useRef(false);
  const handleTextFieldFocus = () => {
    if (!hasFiredTradeSelectorClicked.current) {
      hasFiredTradeSelectorClicked.current = true;
      triggerTradeSelectorClicked(snowplow);
    }
  };

  const handleFindBusiness = async () => {
    const trimmedSearchQuery = searchQuery.trim();
    if (!trimmedSearchQuery) {
      setError(name, STRINGS.emptySearchError);
      return;
    }
    removeError(name);
    triggerAiFindMyBusiness(snowplow, trimmedSearchQuery);
    try {
      const data = await tradesMutation.mutateAsync(trimmedSearchQuery);
      if (!data.results || data.results.length === 0) {
        setError(name, STRINGS.noMatchError);
      }
    } catch {
      setError(name, STRINGS.fetchError);
    }
  };

  const commitTrade = (trade: TradeOption, meta: TradeSelectionMeta) => {
    if (answeredTrade && answeredTrade !== trade.value) {
      setHidden([`${answeredTrade} definition`]);
    }
    setSelectedTrade(trade);
    if (trade.definition) {
      setVisible([`${trade.value} definition`]);
    }
    onChange?.({
      target: {
        name,
        value: trade.value,
        tradeSelectionMeta: meta,
      },
    });
    redirect({
      currentVertical: vertical,
      nextVertical: trade.vertical,
      verticalsWithoutRedirect: trade.verticalsWithoutRedirect,
      affclie,
      journeyId,
      trade: trade.value,
      allowTradeBasedRedirect: Boolean(allowTradeBasedRedirect),
    });
  };

  const { handleSelectTrade: handleTradeSelect, modalProps } =
    useAmbiguousTradeInterceptor<TradeSelectionMeta>(commitTrade, () => {
      if (answeredTrade) {
        setHidden([`${answeredTrade} definition`]);
      }
      setSearchQuery("");
      setSelectedTrade(EMPTY_TRADE_OPTION);
      tradesMutation.reset();
    });

  const handleTradeChangeClick = () => {
    triggerTradeChanged(snowplow, questionFieldProps.id, answeredTrade);
    setSearchQuery("");
    setSelectedTrade(EMPTY_TRADE_OPTION);
  };

  return (
    <div className="mobius-ai-trade-question">
      <QuestionLabel {...questionLabelProps} labelProps={{ id: labelId }} />

      {selectedTrade.value ? (
        <TradeQuestionSelected
          trade={{ ...selectedTrade, definition: undefined }}
          onEditTrade={handleTradeChangeClick}
        />
      ) : (
        <>
          <TextField
            prefixInside={<Icon icon={search} />}
            placeholder={placeholder || STRINGS.defaultPlaceholder}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onFocus={handleTextFieldFocus}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                void handleFindBusiness();
              }
            }}
            errorMessage={errorMessage}
            isInvalid={!!errorMessage}
            aria-labelledby={labelId}
          />

          <div className="mobius-ai-trade-question__button">
            <Button
              onClick={() => void handleFindBusiness()}
              isDisabled={tradesMutation.isPending}
            >
              {tradesMutation.isPending
                ? STRINGS.searching
                : STRINGS.findMyBusiness}
              {tradesMutation.isPending ? (
                <Icon icon={loading} spin />
              ) : (
                <Icon icon={rightArrow} />
              )}
            </Button>
          </div>

          {typeof occupationsPageUrl === "string" && (
            <div className="mobius-trade-question__note">
              {STRINGS.occupationsPrefix}
              <Link href={occupationsPageUrl}>
                {isUK ? STRINGS.occupationsLinkGB : STRINGS.occupationsLinkUS}
              </Link>
            </div>
          )}

          {/* Render trades list */}
          <div className="mobius-ai-trade-question__results">
            <TradesResults
              mutation={tradesMutation}
              variant="ai"
              onTradeSelect={handleTradeSelect}
            />
          </div>
        </>
      )}
      <AmbiguousTradeModal {...modalProps} />
    </div>
  );
};
